import React, { useContext, useEffect, useState } from "react";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import breakpoints from "assets/theme/base/breakpoints";
import { useDispatch } from "react-redux";
import {
  fetchOrganisations,
  getUserDisplayMode,
  setDisplayMode,
  tableLayoutAvailability,
  getInventoryPosConfig,
  logoutSession,
  fetchAccountByOrg,
  getUserRolesById,
} from "../../../../../../config/Services";
import { fetchSubscriptionBundle } from "../../../../../../services/subscriptionService";
import "./indexV2.css";
import { useSelector } from "react-redux";
import { getMobilePos } from "../../redux/Reducers/paginationReducer";
import { UserContext } from "../../../../../../App";
import { emit } from "react-native-react-bridge/lib/web";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import { usePrinterAgentStatus } from "../../../../../../context/PrinterAgentContext";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoutIcon from "@mui/icons-material/Logout";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import PalletLogo from "../../../../../../assets/images/Pallet-logo-blue-transparent (2).png";
import CustomScrollRow from "./CustomScrollRow";

const AVATAR_COLORS = [
  { bg: "rgba(59,130,246,0.12) ", color: "#1d4ed8" }, // blue  — Pallet Retail
  { bg: "rgba(16,185,129,0.12)", color: "#065f46" }, // emerald — Trailweaves
  { bg: "rgba(139,92,246,0.12)", color: "#6d28d9" }, // violet — Bonton
  { bg: "rgba(245,158,11,0.12)", color: "#92400e" }, // amber  — Demo
  { bg: "rgba(239,68,68,0.12)", color: "#991b1b" },
  { bg: "rgba(20,184,166,0.12)", color: "#0f766e" },
];

const getInitials = (name = "") => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const LocationModalV2 = () => {
  const { initializePrinterAgent } = usePrinterAgentStatus();
  const { setSaleMode } = useContext(UserContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [retail, setRetail] = useState([]);
  const [contextIds, setContextIds] = useState(new Set());
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [focusSection, setFocusSection] = useState("org");
  const [focusedOrgIdx, setFocusedOrgIdx] = useState(0);
  const [focusedLocIdx, setFocusedLocIdx] = useState(0);
  const [orgSearch, setOrgSearch] = useState("");
  const [locSearch, setLocSearch] = useState("");
  const isMobilePos = useSelector(getMobilePos);
  const isMobileDevice = useMediaQuery(`(max-width: 576px)`);
  const showSnackbar = useSnackbar();
  const retailType = localStorage.getItem("retailType");

  useEffect(() => {
    fetchingOrganisation();
  }, []);

  const fetchingOrganisation = () => {
    const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
    const uidx = retailUserDetails?.uidx;

    fetchOrganisations()
      .then((res) => {
        if (res?.data?.data?.status === "ERROR") {
          localStorage.setItem("user_roles", []);
          localStorage.setItem("user_name", "");
          showSnackbar(res?.data?.data?.message || "Some Error Occurred", "error");
          return;
        }
        const userDetails = res?.data?.data?.userDetails;
        const retails = res?.data?.data?.retails;

        localStorage.setItem("user_roles", JSON.stringify(userDetails?.roles || []));
        localStorage.setItem("user_name", userDetails?.firstName + " " + userDetails?.secondName);

        const contexts = new Set();
        const userContext = userDetails?.contexts;
        for (const role in userContext) {
          for (const meta of userContext[role].meta || []) {
            contexts.add(meta.contextId);
          }
        }
        setContextIds(contexts);

        const result = retails?.map((r) => ({ label: r?.displayName, value: r?.retailId }));
        localStorage.setItem("Orgnames", JSON.stringify(result));
        isMobilePos && emit({ type: "localData", data: { Orgnames: JSON.stringify(result) } });

        setRetail(retails);
        setLoading(false);
      })
      .catch((err) => {
        showSnackbar(err?.message || "Failed to load organisations", "error");
        setLoading(false);
      });

    getUserDisplayMode(uidx).then((response) => {
      if (response.data?.data.code === 200) {
        setDisplayMode({ uidx, displayMode: retailType ? "TOUCHSCREEN" : "KEYBOARD" }).catch(
          () => {}
        );
        localStorage.setItem("mode", "TOUCHSCREEN");
      } else {
        const mode = response.data?.data?.displayMode === "KEYBOARD" ? "KEYPAD" : "TOUCHSCREEN";
        localStorage.setItem("mode", mode);
      }
    });
  };

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    const payload = {
      metaData: {
        os: navigator.platform,
        latitude: "",
        browser: window.navigator.userAgent,
        city: "",
        country: "",
        postCode: "",
        longitude: "",
        device: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
          navigator.userAgent
        )
          ? "This device is Mobile"
          : /Windows/i.test(navigator.userAgent)
          ? "This device is Desktop"
          : "This device is tablet",
      },
    };

    logoutSession(payload)
      .then((res) => {
        showSnackbar("Successfully Logged out", "success");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        navigate("/");
        setIsLoggingOut(false);
      })
      .catch((err) => {
        showSnackbar(err.message || "Logout failed", "error");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        setIsLoggingOut(false);
      });
  };

  const handleLocation = (
    locationId,
    organizationId,
    contextType,
    sourceApp,
    organisationName,
    locationName,
    branch,
    branchSubType
  ) => {
    setIsNavigating(true);
    /* Notify SubscriptionContext to clear the stale bundle immediately */
    window.dispatchEvent(new CustomEvent("pos:locationChanging"));
    ["locId", "sessionId", "storeMode", "logoUrl", "licenseId"].forEach((k) => {
      if (localStorage.getItem(k)) localStorage.removeItem(k);
    });
    localStorage.setItem("contextType", contextType);
    localStorage.setItem("sourceApp", sourceApp);
    localStorage.setItem("orgId", organizationId);
    localStorage.setItem("locId", locationId);
    localStorage.setItem("orgName", organisationName);
    localStorage.setItem("LocName", locationName);
    localStorage.setItem("retailType", branch?.retailType);
    localStorage.setItem("branchSubType", branchSubType);
    /* Clear stale roles immediately; fresh roles fetched below (non-blocking) */
    localStorage.removeItem("selectedLocationRoles");
    localStorage.removeItem("userPermissions");
    /* Stamp the requested org+location for stale-response detection */
    const _locKey = `${organizationId}:${locationId}`;
    /* Fetch roles + permissions for this location (non-blocking) */
    try {
      const _rud = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
      if (_rud?.uidx && organizationId) {
        getUserRolesById(_rud.uidx, organizationId, locationId)
          .then((r) => {
            /* Discard if user has already moved to a different org or location */
            const currentKey = `${localStorage.getItem("orgId")}:${localStorage.getItem("locId")}`;
            if (currentKey !== _locKey) return;
            if (r?.data?.status === "SUCCESS" || r?.data?.data) {
              const _d = r?.data?.data || {};
              const _roles = _d?.roles || _d?.selectedLocationRoles || [];
              const _perms = _d?.userPermissions || _d?.permissions || {};
              localStorage.setItem("selectedLocationRoles", JSON.stringify(_roles));
              localStorage.setItem("userPermissions", JSON.stringify(_perms));
            }
            /* Always notify RBAC listeners so the spinner is never stuck */
            window.dispatchEvent(new CustomEvent("pos:rolesLoaded"));
          })
          .catch(() => {
            /* Roles fetch failed — fire the event so RbacGate resolves the
               spinner immediately (canViewScreen will read the cleared
               localStorage and return the safe denied/allowed state). */
            const currentKey = `${localStorage.getItem("orgId")}:${localStorage.getItem("locId")}`;
            if (currentKey === _locKey) {
              window.dispatchEvent(new CustomEvent("pos:rolesLoaded"));
            }
          });
      }
    } catch (_) {}
    /* Fetch and cache the real AC* accountId, then fetch subscription bundle (non-blocking) */
    localStorage.removeItem("accountId");
    fetchAccountByOrg(organizationId)
      .then((r) => {
        const aid = r?.data?.data?.data?.accountId;
        /* Only write accountId if still on the same location */
        const currentKey = `${localStorage.getItem("orgId")}:${localStorage.getItem("locId")}`;
        if (aid && currentKey === _locKey) localStorage.setItem("accountId", aid);
        /* Fetch subscription after accountId resolves */
        return fetchSubscriptionBundle(aid || organizationId, organizationId, locationId);
      })
      .then((bundle) => {
        /* Only write if the user is still on the same location */
        const currentKey = `${localStorage.getItem("orgId")}:${localStorage.getItem("locId")}`;
        if (bundle && currentKey === _locKey) {
          try {
            localStorage.setItem("storeSubscription", JSON.stringify(bundle));
          } catch (_) {}
          window.dispatchEvent(new CustomEvent("pos:subscriptionLoaded"));
        }
      })
      .catch(() => {});

    if (isMobilePos) {
      emit({
        type: "localData",
        data: {
          contextType,
          sourceApp,
          orgId: organizationId,
          locId: locationId,
          orgName: organisationName,
          LocName: locationName,
        },
      });
      setIsNavigating(false);
      navigate("/dashboard");
      return;
    }

    setSaleMode(branch?.retailType === "RESTAURANT" ? "DINE_IN" : "in-store");
    setIsNavigating(false);
    localStorage.removeItem("CartId");
    if (!localStorage.getItem("printerConfig")) {
      const defaultPrinterConfig = {
        value: "posiflex-usb",
        label: "USB port",
        printer: "Posiflex",
      };
      localStorage.setItem("printerConfig", JSON.stringify(defaultPrinterConfig));
    }
    if (branch?.retailType === "RESTAURANT") {
      initializePrinterAgent();
      tableLayoutAvailability(locationId)
        .then((res) => {
          localStorage.setItem("layoutId", res?.data?.object?.[0]?.layoutId);
          return getInventoryPosConfig(organizationId, locationId);
        })
        .then((configRes) => {
          if (configRes?.data) {
            localStorage.setItem("isInventoryConfigSet", configRes?.data?.data?.es === 0);
            const enable = configRes?.data?.data?.data?.enableMandatoryStockCount;
            if (enable !== undefined)
              localStorage.setItem("enableMandatoryStockCount", enable.toString());
          }
        })
        .catch(() => {});
    }

    if (branch?.retailType === "RESTAURANT") {
      navigate("/products/access-mode");
      return;
    }
    navigate("/dashboard");
  };

  const accessibleRetail = retail?.filter((r) =>
    r?.branches?.some((b) => contextIds?.has(b?.branchId))
  );

  const filteredOrgs = accessibleRetail?.filter(
    (r) => !orgSearch || r?.displayName?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  const selectedOrgData = accessibleRetail.find((r) => r.retailId === selectedOrg);
  const accessibleBranches =
    selectedOrgData?.branches?.filter((b) => contextIds.has(b?.branchId)) || [];

  const filteredBranches = accessibleBranches?.filter(
    (b) =>
      !locSearch ||
      b?.displayName?.toLowerCase().includes(locSearch.toLowerCase()) ||
      (b?.stateName || b?.state || b?.city || "").toLowerCase().includes(locSearch.toLowerCase())
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (focusSection === "org") {
        if (e?.key === "ArrowDown") {
          e.preventDefault();
          setFocusedOrgIdx((prev) => Math.min(prev + 1, filteredOrgs?.length - 1));
        } else if (e?.key === "ArrowUp") {
          e.preventDefault();
          setFocusedOrgIdx((prev) => Math.max(prev - 1, 0));
        } else if (e?.key === "Enter") {
          e.preventDefault();
          const org = filteredOrgs[focusedOrgIdx];
          if (org) {
            setSelectedOrg(org?.retailId);
            setFocusSection("location");
            setFocusedLocIdx(0);
          }
        }
      } else if (focusSection === "location") {
        if (e?.key === "ArrowDown") {
          e.preventDefault();
          setFocusedLocIdx((prev) => Math.min(prev + 1, filteredBranches?.length - 1));
        } else if (e?.key === "ArrowUp") {
          e.preventDefault();
          setFocusedLocIdx((prev) => Math.max(prev - 1, 0));
        } else if (e?.key === "Enter") {
          e.preventDefault();
          const branch = filteredBranches[focusedLocIdx];
          if (branch && !isNavigating) {
            handleLocation(
              branch?.branchId,
              selectedOrgData?.retailId,
              "RETAIL",
              "PALLET_RETAIL",
              selectedOrgData?.displayName,
              branch?.displayName,
              selectedOrgData,
              branch?.branchSubType
            );
          }
        } else if (e?.key === "Escape") {
          e.preventDefault();
          setFocusSection("org");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    focusSection,
    focusedOrgIdx,
    focusedLocIdx,
    filteredOrgs,
    filteredBranches,
    selectedOrgData,
    isNavigating,
  ]);

  useEffect(() => {
    document
      .querySelector(".sl-kb-focused")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [focusedOrgIdx, focusedLocIdx, focusSection]);

  return (
    <>
      {isLoggingOut && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "not-allowed",
          }}
        />
      )}
      <div className="sl-page" style={{ opacity: isLoggingOut ? 0.8 : 1 }}>
        {/* ── Header card ── */}
        <div className="sl-header-card">
          <header className="sl-header">
            <div className="sl-header-left">
              <img src={PalletLogo} alt="Pallet" className="sl-logo" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <span className="sl-step-badge">Step 1 of 4</span>
              <div
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: isLoggingOut ? "not-allowed" : "pointer",
                  color: "#e53e3e",
                }}
                title="Logout"
              >
                {isLoggingOut ? (
                  <ArrowPathIcon className="spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  <LogoutIcon sx={{ fontSize: "1.25rem" }} />
                )}
                <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>Logout</span>
              </div>
            </div>
          </header>
        </div>
        {/* end sl-header-card */}

        {/* ── Body (on page background) ── */}
        <main className="sl-body">
          {loading ? (
            <div className="sl-loading">
              <CircularProgress size={28} sx={{ color: "#2A71FF" }} />
            </div>
          ) : (
            <div className="sl-two-col">
              {/* ── LEFT: Organisation panel ── */}
              <section className="sl-panel sl-panel-org" onClick={() => setFocusSection("org")}>
                <div className="sl-panel-header">
                  <div>
                    <p className="sl-section-tag">Organization</p>
                    <h1 className="sl-section-title">Select your organization</h1>
                  </div>
                  <span className="sl-choose-label">Choose one</span>
                </div>

                {/* Search */}
                <div className="sl-search-wrap">
                  <svg
                    className="sl-search-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <circle cx="6" cy="6" r="4.5" stroke="#94a3b8" strokeWidth="1.4" />
                    <path
                      d="M9.5 9.5l2.5 2.5"
                      stroke="#94a3b8"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    className="sl-search-input"
                    placeholder="Search organizations…"
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {orgSearch && (
                    <button
                      className="sl-search-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOrgSearch("");
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Org list */}
                <div className="sl-panel-list">
                  {filteredOrgs?.length === 0 && (
                    <p className="sl-empty-msg">No organizations found</p>
                  )}
                  {filteredOrgs?.map((org, idx) => {
                    const av = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                    const isSelected = selectedOrg === org?.retailId;
                    return (
                      <div
                        key={org?.retailId}
                        className={`sl-org-row${isSelected ? " selectedb" : ""}${
                          focusSection === "org" && focusedOrgIdx === idx ? " sl-kb-focused" : ""
                        }`}
                        onClick={() => {
                          setSelectedOrg(org?.retailId);
                          setFocusedOrgIdx(idx);
                          setFocusSection("location");
                          setLocSearch("");
                        }}
                      >
                        <div className="sl-avatar" style={{ background: av.bg, color: av.color }}>
                          {getInitials(org?.displayName)}
                        </div>
                        <div className="sl-org-row-text">
                          <p className="sl-org-name">{org?.displayName}</p>
                          <p className="sl-org-desc">
                            {org?.description || org?.retailType || "—"}
                          </p>
                        </div>
                        <span className="sl-org-count-pill">
                          {org?.branches?.length || 0} stores
                        </span>
                        {isSelected && (
                          <div className="sl-check-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="m9 12 2 2 4-4" />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ── RIGHT: Location panel ── */}
              <section
                className="sl-panel sl-panel-loc"
                onClick={() => selectedOrg && setFocusSection("location")}
              >
                <div className="sl-panel-header">
                  <div>
                    <p className="sl-section-tag">Location</p>
                    <h2 className="sl-section-title">
                      {selectedOrg
                        ? `Locations — ${selectedOrgData?.displayName}`
                        : "Select a location"}
                    </h2>
                  </div>
                  {selectedOrg && (
                    <span className="sl-org-badge">
                      {filteredBranches?.length} location{filteredBranches?.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Search */}
                <div className={`sl-search-wrap${!selectedOrg ? " sl-search-disabled" : ""}`}>
                  <svg
                    className="sl-search-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <circle cx="6" cy="6" r="4.5" stroke="#94a3b8" strokeWidth="1.4" />
                    <path
                      d="M9.5 9.5l2.5 2.5"
                      stroke="#94a3b8"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    className="sl-search-input"
                    placeholder="Search locations…"
                    value={locSearch}
                    disabled={!selectedOrg}
                    onChange={(e) => setLocSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {locSearch && (
                    <button
                      className="sl-search-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocSearch("");
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Location list or empty state */}
                <div className="sl-panel-list">
                  {!selectedOrg ? (
                    <div className="sl-loc-empty-state">
                      <div className="sl-loc-empty-icon">
                        <LocationOnIcon sx={{ fontSize: "2rem", color: "#cbd5e1" }} />
                      </div>
                      <p className="sl-loc-empty-title">No organization selected</p>
                      <p className="sl-loc-empty-sub">
                        Pick an organization on the left to see its locations here.
                      </p>
                    </div>
                  ) : filteredBranches?.length === 0 ? (
                    <p className="sl-empty-msg">No locations found</p>
                  ) : (
                    filteredBranches?.map((branch, idx) => {
                      const status = branch?.branchStatus || branch?.status || "ACTIVE";
                      const isActive = status?.toUpperCase() === "ACTIVE";
                      const state = branch?.stateName || branch?.state || branch?.city || "Store";
                      return (
                        <div
                          key={branch?.branchId}
                          className={`sl-loc-row${
                            focusSection === "location" && focusedLocIdx === idx
                              ? " sl-kb-focused"
                              : ""
                          }`}
                          onClick={
                            !isNavigating
                              ? () =>
                                  handleLocation(
                                    branch?.branchId,
                                    selectedOrgData?.retailId,
                                    "RETAIL",
                                    "PALLET_RETAIL",
                                    selectedOrgData?.displayName,
                                    branch?.displayName,
                                    selectedOrgData,
                                    branch?.branchSubType
                                  )
                              : undefined
                          }
                        >
                          <div className="sl-loc-row-left">
                            <div className="sl-loc-pin-wrap">
                              <LocationOnIcon sx={{ fontSize: "1rem", color: "#2a71ff" }} />
                            </div>
                            <div>
                              <p className="sl-loc-name">{branch?.displayName}</p>
                              <p className="sl-loc-state">{state}</p>
                            </div>
                          </div>
                          <span className={`sl-status-badge ${isActive ? "active" : "ready"}`}>
                            {isActive ? "Active" : "Ready"}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default LocationModalV2;
